/**
 * Asset Selection State Management Hook
 *
 * Manages row selection state for the asset table, including:
 * - Selected asset keys (key-based selection for reliability)
 * - Computed selected assets from keys
 * - Helper methods for selection operations
 * - Automatic clearing when filters change
 *
 * Risk Level: Low
 * - Has one useEffect for clearing selection on filter change
 * - Uses proper React dependencies for optimal performance
 * - Key-based selection survives pagination, sorting, and filtering
 * - Gracefully handles filtered or deleted assets
 */

import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { Filters } from '@/hooks/useAssets';
import type { Asset } from '@/types';

/**
 * Return type for useAssetSelection hook
 */
interface AssetSelectionReturn {
  /** Array of selected asset keys */
  selectedKeys: string[];
  /** Direct setter for selected keys array (for multi-select support) */
  setSelectedKeys: (keys: string[]) => void;
  /** Boolean indicating if any rows are selected */
  isRowsSelected: boolean;
  /** Array of actual asset objects that are selected */
  selectedAssets: Asset[];
  /** Function to toggle selection of an asset by key */
  toggleSelection: (key: string) => void;
  /** Function to clear all selections */
  clearSelection: () => void;
  /** Function to select all assets */
  selectAll: () => void;
}

/**
 * Hook to manage row selection state for asset table
 *
 * Uses key-based selection for reliability across pagination, filtering, and sorting.
 * Provides helper methods for common selection operations.
 *
 * Uses proper React dependency tracking for optimal performance:
 * - useMemo with direct dependencies (no JSON.stringify overhead)
 * - useRef + deep comparison for complex filter object changes
 *
 * @param assets - Array of all assets in the table
 * @param filters - Current filter state (used to clear selection on change)
 * @returns Object containing selection state and computed values
 *
 * @example
 * ```tsx
 * const { selectedKeys, setSelectedKeys, selectedAssets, toggleSelection, clearSelection } = useAssetSelection(assets, filters);
 *
 * // Use in table selection
 * <Table
 *   selection={{ value: selectedKeys, onChange: setSelectedKeys }}
 * />
 *
 * // Use selected assets for actions
 * <AssetActions assets={selectedAssets} />
 *
 * // Toggle individual asset
 * toggleSelection(asset.key);
 *
 * // Clear all selections
 * clearSelection();
 * ```
 */
export const useAssetSelection = (
  assets: Asset[],
  filters: Filters
): AssetSelectionReturn => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const filtersRef = useRef(filters);

  // Compute selected assets from keys
  // Uses key-based lookup for reliability across pagination and filtering
  const { isRowsSelected, selectedAssets } = useMemo(() => {
    const updatedSelectedAssets = selectedKeys
      .map(key => {
        const asset = assets.find(a => a.key === key);

        if (!asset) {
          // Asset no longer in current view (filtered out, deleted, etc.)
          // This is expected behavior and not an error
          return null;
        }

        return asset;
      })
      .filter((asset): asset is Asset => asset !== null);

    return {
      isRowsSelected: selectedKeys.length > 0,
      selectedAssets: updatedSelectedAssets,
    };
  }, [selectedKeys, assets]);

  // Clear selection when filters change
  useEffect(() => {
    if (!isEqual(filtersRef.current, filters)) {
      setSelectedKeys([]);
      filtersRef.current = filters;
    }
  }, [filters]);

  // Toggle selection for a single asset
  const toggleSelection = (key: string) => {
    setSelectedKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedKeys([]);
  };

  // Select all assets in current view
  const selectAll = () => {
    setSelectedKeys(assets.map(asset => asset.key));
  };

  return {
    selectedKeys,
    setSelectedKeys,
    isRowsSelected,
    selectedAssets,
    toggleSelection,
    clearSelection,
    selectAll,
  };
};
